
import React, { useEffect, useState } from 'react'
import { Tab } from 'react-bootstrap'
import { useHistory, useLocation } from 'react-router-dom'

import useTranslation from '@/Hooks/UseTranslation'
import { usePlan } from '@/Hooks/UsePlan'

import MainLayout from '@/Layouts/MainLayout/MainLayout'
import ContentHeader from '@/Layouts/MainLayout/components/ContentHeader'

import ExportModal from '@/Components/ExportModal/ExportModal'
import Icon from '@/Components/Icon/Icon'
import MsProjectImporter from '@/Components/MsProjectImporter/MsProjectImporter'
import ArchivedProjects from './tabs/ArchivedProjects'
import { useDispatch, useSelector } from 'react-redux'
import OtherProjects from './tabs/OtherProjects'
import { cleanProgrammationAction } from '@/Redux/Actions/ProgrammationActions'
import { cleanMacro } from '@/Redux/Actions/MacroActions'
import { cleanLookahead } from '@/Redux/Actions/LookaheadActions'
import { cleanWeekly } from '@/Redux/Actions/WeeklyActions'
import { cleanTasksAction } from '@/Redux/Actions/TasksActions'
import { cleanResourcesCNCAction } from '@/Redux/Actions/ResourcesCNCActions'
import { cleanIndicatorsPPCAction } from '@/Redux/Actions/IndicatorsPPCActions'
import { cleanResourcesRestrictionsAction } from '@/Redux/Actions/ResourcesRestrictionsActions'
import TeamsActions from '@/Redux/Actions/TeamsActions'
import ProjectCNCActions from '@/Redux/Actions/ProjectCNCActions'
import MainContentBody from '@/Layouts/MainLayout/components/MainContentBody'
import { PROJECTS_NAVIGATION, ProjectsTab } from './projects-navigation'
import ProjectsActions from '@/Redux/Actions/ProjectsActions'
import { cleanDashboardAction } from '@/Redux/Actions/DashboardActions'
import MyProjects from './tabs/MyProjects'

const HomePage = () => {

  const { t } = useTranslation()
  const history = useHistory()

  const {
    plan,
    tooManyActiveProjects,
    tooManyOthersProjects,
    showTooManyProjectsModal,
    showTooManyOthersProjectsModal,
  } = usePlan()

  const dispatch = useDispatch()

  const location = useLocation()

  const getRequestedTab = (): ProjectsTab => {
    const requestedTab = new URLSearchParams(location.search).get('tab')

    return PROJECTS_NAVIGATION.some((item) => item.id === requestedTab)
      ? requestedTab as ProjectsTab
      : 'mine'
  }

  const [ tab, setTab ] = useState<ProjectsTab>(getRequestedTab)

  const projects = useSelector((state: any) => state.ProjectsReducer.data)

  const [ showCreateModal, setShowCreateModal ] = useState(false)
  const [ showExportModal, setShowExportModal ] = useState(false)
  const [ showMsProjectImporter, setShowMsProjectImporter ] = useState(false)

  const myProjectsCount = projects?.mine?.length || 0
  const sharedProjectsCount = projects?.other?.length || 0
  const archivedProjectsCount = projects?.archived?.length || 0
  const activeProjectsCount = myProjectsCount + sharedProjectsCount

  const buttons = [
    {
      label: t('HOME:NewProjectButton'),
      icon: <i className="fas fa-plus" aria-hidden="true" />,
      onClick: () => handleCreatePressed(),
      className: 'pl4-page-action--primary',
      mobilePrimary: true,
      disabled: tab !== 'mine'
    },
    {
      label: t('HOME:ExportButton'),
      icon: <Icon name="download" fas style={{ marginRight: 4 }} />,
      onClick: () => setShowExportModal(true),
      className: 'pl4-page-action--secondary',
      disabled: tab !== 'mine'
    },
    {
      label: t('HOME:ImportMsProjectButton'),
      icon: <Icon name="upload" fas style={{ marginRight: 4 }} />,
      onClick: () => handleImportMsProjectPressed(),
      disabled: tab !== 'mine'
    }
  ]

  const tabCounts = {
    mine: myProjectsCount,
    others: sharedProjectsCount,
    archived: archivedProjectsCount,
  }

  const handleChangeTab = (nextTab: ProjectsTab) => {
    if (nextTab === tab) return

    history.push(PROJECTS_NAVIGATION.find((item) => item.id === nextTab)?.path || '/')
  }

  const tabs = PROJECTS_NAVIGATION.map((item) => ({
    label: t(item.translationKey),
    icon: <i className={item.icon} />,
    onClick: () => handleChangeTab(item.id),
    active: tab === item.id,
    badge: tabCounts[item.id],
  }))

  const handleCreatePressed = () => {
    if (plan.can('create', 'Project')) {

      setShowCreateModal(true);
    } else {

      showTooManyProjectsModal(t('HOME:TooManyProjects-title'))
    }
  }

  const handleImportMsProjectPressed = () => {
    if (plan.can('create', 'Project')) {

      setShowMsProjectImporter(true)
    } else {

      showTooManyProjectsModal(t('HOME:TooManyProjects-title'))
    }
  }

  useEffect(() => {

    setTab(getRequestedTab())
  }, [location.search])

  useEffect(() => {

    const loadProjects = async () => {

      await dispatch(ProjectsActions.getResultAction())
    }

    loadProjects()
  }, [])

  useEffect(() => {

    const clearData = () => {

      dispatch(ProjectsActions.clearProjectAction())
      dispatch(cleanDashboardAction())
      dispatch(cleanProgrammationAction())
      dispatch(cleanMacro())
      dispatch(cleanLookahead())
      dispatch(cleanWeekly())
      dispatch(cleanTasksAction())
      dispatch(cleanResourcesCNCAction())
      dispatch(cleanIndicatorsPPCAction())
      dispatch(cleanResourcesRestrictionsAction())
      dispatch(TeamsActions.clearDataAction())
      dispatch(ProjectCNCActions.clearAction())

      dispatch({
        type: 'DASHBOARD_LOAD_NONE'
      })
    }

    clearData()
  }, [])

  useEffect(() => {

    if (tooManyActiveProjects)
      showTooManyProjectsModal(t('HOME:TooManyActiveProjects-title'))

    if (tooManyOthersProjects)
      showTooManyOthersProjectsModal(t('HOME:TooManyProjectsYouAreParticipatingIn-title'))
  }, [tooManyActiveProjects, tooManyOthersProjects, projects.active])

  return (
    <>
      <MainLayout menuName="homepage" subMenuName={tab} className="pl4-home-layout">
        <div className="pl4-home-page">
          <ContentHeader
            title={t('HOME:Page-title')}
            subtitle={`${activeProjectsCount} ${t('HOME:ActiveProjectsSubtitle')}`}
            buttons={buttons}
            navTabs={tabs}
            className="pl4-home-header pl4-page-header"
          />
          <MainContentBody>
            <div className="pl4-home-board">
              <div className="pl4-home-board__main">
                <Tab.Content className='no-padding'>
                  <Tab.Pane active={tab === 'mine'}>
                    <MyProjects
                      showCreateModal={showCreateModal}
                      setShowCreateModal={setShowCreateModal}
                      onCreateProject={handleCreatePressed}
                    />
                  </Tab.Pane>
                  <Tab.Pane active={tab === 'others'}>
                    <OtherProjects />
                  </Tab.Pane>
                  <Tab.Pane active={tab === 'archived'}>
                    {tab === 'archived' && <ArchivedProjects />}
                  </Tab.Pane>
                </Tab.Content>
              </div>
              <aside className="pl4-home-summary" aria-label="Resumen ejecutivo de proyectos">
              <div className="pl4-home-summary__heading">
                <span>Resumen ejecutivo</span>
                <small>Todos los proyectos</small>
              </div>
              <div className="pl4-home-summary__block">
                <strong>{t('HOME:ProjectsSummaryTitle')}</strong>
                <div className="pl4-home-summary__status">
                  <div className="pl4-home-summary__donut pl4-home-summary__donut--total">
                    <div><strong>{activeProjectsCount}</strong><span>{t('HOME:ActiveProjectsLabel')}</span></div>
                  </div>
                  <div className="pl4-home-summary__legend">
                    <span><i className="pl4-home-summary__legend-dot--mine" /> {t('HOME:MyProjectsTab')} <strong>{myProjectsCount}</strong></span>
                    <span><i className="pl4-home-summary__legend-dot--shared" /> {t('HOME:SharedProyectsTab')} <strong>{sharedProjectsCount}</strong></span>
                    <span><i className="pl4-home-summary__legend-dot--archived" /> {t('HOME:ArchivedProjectsTab')} <strong>{archivedProjectsCount}</strong></span>
                  </div>
                </div>
              </div>
              <div className="pl4-home-summary__block pl4-home-summary__note">
                <strong>{t('HOME:ProgressSummaryTitle')}</strong>
                <span>{t('HOME:ProgressSummaryUnavailable')}</span>
              </div>
              </aside>
            </div>
          </MainContentBody>
        </div>
      </MainLayout>

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={(format: string) => console.log(format)}
      />

      <MsProjectImporter
        show={showMsProjectImporter}
        onHide={() => setShowMsProjectImporter(false)}
        onImported={() => dispatch(ProjectsActions.getResultAction())}
        onOpenProject={(projectId) => history.push(`/project/${projectId}`)}
      />
    </>
  )
}

export default HomePage
